import React from 'react';
import styled from '@emotion/styled';
import { isNumber } from 'lodash';
import Grid, { type RegularBreakpoints } from '@mui/material/Grid';
import {
  type GridSpacing,
  spacing as muiSpacing,
  type ResponsiveStyleValue,
  type GridProps as MuiGridProps,
} from '@mui/system';

import { GRID, BREAKPOINTS } from '@/constants';

type GutterLeftMargins = number[];

type ColProps = MuiGridProps &
  Record<string, unknown> & {
    // This offset has nothing to do with the MUI System offset.
    // Which is here: https://v5.mui.com/system/react-grid/#offset
    offset?: GutterLeftMargins;
    spacing?: ResponsiveStyleValue<GridSpacing>;
    width?: RegularBreakpoints;
    className?: string;
    children: React.ReactNode | JSX.Element | string;
  };

// This is what offset is used for. Offset is an array up to 5 items long.
// Each item is expected to be a number between 0 and 1 (exclusively).
// These numbers are converted into percentages, and are used to add left
// margin to the element.
// Historically, this is done to make divs appear centered.
// This should be cleaned.
const toPercent = (offset?: number) => (offset ? `${offset * 100}%` : undefined);

const getGutterStyles = (offset: number[] = []) => `
  &.MuiGrid-item {
    margin-left: ${offset ? toPercent(offset[0]) : 0};

    @media (min-width: ${BREAKPOINTS.SM}px) {
      margin-left: ${toPercent(offset[1])};
    }

    @media (min-width: ${BREAKPOINTS.MD}px) {
      margin-left: ${toPercent(offset[2])};
    }

    @media (min-width: ${BREAKPOINTS.LG}px) {
      margin-left: ${toPercent(offset[3])};
    }

    @media (min-width: ${BREAKPOINTS.XL}px) {
      margin-left: ${toPercent(offset[4])};
    }
  }
`;

const StyledCol = styled(Grid)<ColProps>`
  ${({ spacing, offset = [] }) => !isNumber(spacing) && offset.length > 0 && getGutterStyles(offset)};

  padding-left: ${GRID.GUTTER_HALF};
  padding-right: ${GRID.GUTTER_HALF};

  ${muiSpacing}
`;

const Col = ({ className, offset, width, ...props }: ColProps) => (
  <StyledCol {...props} {...width} className={className} item offset={offset} />
);

export default Col;
